import React, { useState, useRef } from "react";
import FileUpload from "@/components/FileUpload";
import PDFViewer, { PDFViewerRef } from "@/components/PDFViewer";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export default function Index() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const editedViewerRef = useRef<PDFViewerRef>(null);
  const { toast } = useToast();

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleDownload = async () => {
    if (selectedFile && editedViewerRef.current) {
      try {
        const editedPdfBlob = await editedViewerRef.current.getEditedPdfBlob();
        if (editedPdfBlob) {
          const url = URL.createObjectURL(editedPdfBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `edited_${selectedFile.name}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          toast({
            title: "ダウンロード開始",
            description: `${selectedFile.name}のダウンロードを開始しました`,
          });
        }
      } catch (error) {
        console.error('Download error:', error);
        toast({
          title: "エラー",
          description: "PDFの保存中にエラーが発生しました",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-7xl mx-auto p-4">
        {/* ヘッダー */}
        <div className="text-center mb-8 animate-fade-up">
          <h1 className="text-4xl font-bold mb-2">PDF Editor</h1>
        </div>

        {/* PDFビューアー領域 */}
        <div className="mb-8">
          <div className="border-2 border-dashed border-gray-600 hover:border-blue-500 transition-colors duration-200 bg-gray-800/50 rounded-lg p-4 animate-fade-up">
            {!selectedFile ? (
              <FileUpload onFileSelect={handleFileSelect} />
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-lg">
                    現在のファイル: {selectedFile.name}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedFile(null)}
                  >
                    ファイルを変更
                  </Button>
                </div>
                {/* PDFプレビュー */}
                <PDFViewer
                  ref={editedViewerRef}
                  file={selectedFile}
                />
              </div>
            )}
          </div>
        </div>

        {/* アクションボタン */}
        <div className="flex justify-center animate-fade-up delay-200">
          <Button
            onClick={handleDownload}
            disabled={!selectedFile}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="mr-2 h-4 w-4" />
            編集したPDFをダウンロード
          </Button>
        </div>
      </div>
    </div>
  );
}